import { Button } from '@flywire/headlessui-tailwindcss';
import {
  CardElement,
  type CardElementError,
  type CardElementEvent,
  type CardElementRef,
  type OnBinLookupResult,
  OnSuccessPayload,
  PayerElement,
  PayerInfo,
} from '@flywire/internal-react-elements';
import { LoadingDots } from 'components/ui/LoadingDots/LoadingDots';
import { FLYWIRE_JS_ENVIRONMENT } from 'constants/config';
import { PropsWithChildren, RefObject } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getFieldByEntity } from 'selectors/entities/fields/fields';
import { getFieldIdsAndValues } from 'selectors/ui/ui';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

import '@flywire/internal-react-elements/styles.css';
import type { SenderFields } from 'domain/recurringReceivables/repository/types';
import type { FieldValue } from 'models';

export type CardFormProps = {
  cardFormRef: RefObject<CardElementRef>;
  currency?: string;
  sessionId: string;
  onBinLookup?: (result: OnBinLookupResult) => void;
  onError?: (error: CardElementError) => void;
  onSuccess: (data: OnSuccessPayload) => void;
  onStartLoading: () => void;
  onEndLoading: () => void;
  onEvent?: (event: CardElementEvent) => void;
  senderFields?: SenderFields;
};

const CardForm = ({
  cardFormRef,
  currency,
  children,
  sessionId,
  onBinLookup,
  onError,
  onSuccess,
  onEvent,
  onStartLoading,
  onEndLoading,
  senderFields,
}: PropsWithChildren<CardFormProps>) => {
  const { payerInfo } = usePayerInfo(senderFields);

  const _onError = async (error: CardElementError) => {
    switch (error.type) {
      case 'challenge_error':
      case 'invalid_card_info':
      case 'invalid_payer_info':
      case 'not_found':
        break;
      case 'internal_error':
        errorNotifier.notify(
          new Error(`CardForm error: ${JSON.stringify(error)}`),
        );
    }

    onEndLoading();
    onError?.(error);
  };

  return (
    <CardElement
      currency={currency}
      environment={FLYWIRE_JS_ENVIRONMENT}
      onBinLookup={onBinLookup}
      onEndLoading={onEndLoading}
      onError={_onError}
      onStartLoading={onStartLoading}
      onSuccess={onSuccess}
      payerInfo={payerInfo}
      sessionId={sessionId}
      onEvent={onEvent}
      ref={cardFormRef}
    >
      {children}
    </CardElement>
  );
};

const Form = ({
  buttonDisabled = false,
  buttonLabel,
  isLoading = false,
  onSubmit,
}: {
  buttonDisabled?: boolean;
  buttonLabel: string;
  isLoading?: boolean;
  onSubmit?: () => void;
}) => {
  const disabled = isLoading || buttonDisabled;

  return (
    <div className="flex flex-col mt-8 gap-8">
      <PayerElement className="grid md:grid-cols-2 !grid-cols-1 md:col-span-2">
        <PayerElement.FirstName />
        <PayerElement.LastName />
      </PayerElement>
      <CardElement.CardNumber className="col-span-2" />
      <CardElement.ExpiryDate />
      <CardElement.CVV />
      <Button
        data-testid="cardFormSubmitBtn"
        onClick={onSubmit}
        disabled={disabled}
      >
        {isLoading ? (
          <LoadingDots className="h-6 items-center [&>span]:bg-white" />
        ) : (
          buttonLabel
        )}
      </Button>
    </div>
  );
};

CardForm.Form = Form;

export { CardForm };

const usePayerInfo = (fields?: SenderFields) => {
  const { ids } = useSelector((state: RootState) =>
    getFieldByEntity(state, 'sender'),
  );
  const senderFields = useSelector((state: RootState) =>
    getFieldIdsAndValues(state, ids),
  );

  let payerInfo: PayerInfo;

  if (fields) {
    const { address1, address2, ...rest } = fields;
    payerInfo = {
      ...rest,
      address: [address1, address2].join(' '),
    };
  } else {
    payerInfo = {
      address: [
        senderFields.sender_address1 as string,
        senderFields.sender_address2 as string,
      ].join(' '),
      city: senderFields.sender_city as string,
      country: senderFields.sender_country as string,
      email: senderFields.sender_email as string,
      firstName: senderFields.sender_first_name as string,
      lastName: senderFields.sender_last_name as string,
      phone: senderFields.sender_phone as string,
      state: senderFields.sender_state as string,
      zip: (senderFields.sender_zip as string) || '',
    };
  }

  return {
    payerInfo,
  };
};
